import {ColorPicker} from '@wix/wix-ui-tpa/cssVars';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import classNames from 'classnames';
import React, {ComponentProps} from 'react';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {withRenderContext, type WithRenderContextProps} from '../../../providers/renderContext';
import {SPECS} from '../../../specs';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import {st, classes as colorOptionStyles} from './ColorOption.st.css';
import s from './ProductColor.scss';

export interface ProductColorsProps extends ProvidedGlobalProps, IProvidedTranslationProps, WithRenderContextProps {
  optionIndex: number;
  colorPickerCounter?: number;
  id?: string;
  showErrors?: boolean;
  showTooltips?: boolean;
}

export enum ProductColorsDataHook {
  Container = 'product-options-color-container',
  ColorOption = 'product-options-color',
  ColorOptionItem = 'product-options-color-item',
  ColorOptionTitle = 'product-colors-title',
}

class ProductColorsComponent extends React.Component<ProductColorsProps> {
  private readonly colorPickerContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: ProductColorsProps) {
    const {
      optionIndex,
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget =
        UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
        optionIndex === scrollAndFocusInputTarget?.index;

      if (isScrollAndFocusInputTarget && this.colorPickerContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.Selection, this.colorPickerContainerRef.current, isQuickView);
      }
    }
  }

  private readonly handleChange: ComponentProps<typeof ColorPicker>['onChange'] = (event, itemId) => {
    const {
      optionIndex,
      globals: {handleUserInput, validateUserInputsChanges, userInputs},
    } = this.props;

    const nextSelection = this.productOption.selections.find((selection) => selection.description === itemId);

    const isDeselected =
      nextSelection && userInputs[UserInputType.Selection].some((selection) => selection?.id === nextSelection.id);

    handleUserInput(UserInputType.Selection, isDeselected ? undefined : nextSelection, optionIndex);

    validateUserInputsChanges();
  };

  private get activeSelection() {
    const {
      optionIndex,
      globals: {userInputs},
    } = this.props;

    return userInputs[UserInputType.Selection][optionIndex] ?? undefined;
  }

  private get productOption() {
    const {
      optionIndex,
      globals: {product},
    } = this.props;

    return product.options[optionIndex];
  }

  private get isGroupedByOption(): boolean {
    const {productGroup, isCombinedListing} = this.props.globals;
    return !!isCombinedListing && this.productOption.id === productGroup?.groupingCustomizationId;
  }

  private get colorPickerOptions(): JSX.Element[] {
    const {
      showTooltips = true,
      globals: {
        variantInfo,
        isBackInStockEnabled,
        product,
        useExperiments,
        productGroup,
        groupedMemberUrls,
        isEditorMode,
      },
    } = this.props;

    const isSwatchImagesEnabled = useExperiments.enabled(SPECS.StorefrontSwatchImages);

    if (this.isGroupedByOption) {
      return productGroup?.members?.map((member, index) => {
        const href = !isEditorMode ? groupedMemberUrls?.[member.slug] : undefined;
        return (
          <a key={index} href={href} className={s.groupedSwatchLink}>
            <ColorPicker.Item
              id={member.choice.description}
              aria-label={member.choice.description}
              value={member.choice.value}
              isCrossedOut={member.inventoryAvailabilityStatus === 'OUT_OF_STOCK'}
              tooltip={showTooltips ? member.choice?.description : undefined}
              checked={member.productId === product.id}
              data-hook={ProductColorsDataHook.ColorOptionItem}
              className={s.colorOptions}
            />
          </a>
        );
      });
    }

    const getSelectionAvailabilityInfo = (selectionId: number) =>
      variantInfo.selectionsAvailability[this.productOption.id][selectionId];

    return this.productOption.selections
      .filter((selection) => getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => {
        const isDisabled = !getSelectionAvailabilityInfo(selection.id).isSelectable;
        return (
          <ColorPicker.Item
            key={selection.id}
            id={selection.description}
            aria-label={selection.description}
            value={selection.value}
            imageSrc={isSwatchImagesEnabled ? selection.displayImage?.url : undefined}
            disabled={!isBackInStockEnabled && isDisabled}
            isCrossedOut={isDisabled}
            tooltip={showTooltips ? selection.description : undefined}
            checked={(this.activeSelection?.id ? [this.activeSelection.id] : []).includes(selection.id)}
            data-hook={ProductColorsDataHook.ColorOptionItem}
            className={s.colorOptions}
          />
        );
      });
  }

  public readonly renderDescription = () => (this.activeSelection ? `: ${this.activeSelection.description}` : '');

  private get groupedDescription(): string {
    const {
      globals: {productGroup, product},
    } = this.props;
    const currentMember = productGroup?.members?.find((m) => m.productId === product.id);
    return currentMember?.choice?.description ? `: ${currentMember.choice.description}` : '';
  }

  private renderColorPicker() {
    const {
      optionIndex,
      showErrors = true,
      globals: {isProductSubmitted, userInputErrors, isQuickView, useExperiments},
      t,
      renderContext,
    } = this.props;

    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StickyAddToCartMobile);
    const shouldShowErrors = showErrors || !isStickyAddToCartEnabled;
    const hasError = isProductSubmitted && userInputErrors[UserInputType.Selection][optionIndex];
    const errorMessage =
      shouldShowErrors && hasError ? t('SELECT_OPTION_WARNING', {optionTitle: this.productOption.title}) : undefined;

    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === 'stickyDrawer';

    return (
      <ColorPicker
        className={classNames(
          st(colorOptionStyles.colorOption, {stickyDrawer: isStickyDrawer}),
          {[s.productOptionsColorContainer]: isQuickView},
          {[s.title]: true}
        )}
        label={`${this.productOption.title}${
          this.isGroupedByOption ? this.groupedDescription : this.renderDescription()
        }`}
        onChange={this.handleChange}
        data-hook={ProductColorsDataHook.ColorOption}
        errorMessage={errorMessage}
        required
        showAsterisk>
        {this.colorPickerOptions}
      </ColorPicker>
    );
  }

  public render(): JSX.Element {
    const {colorPickerCounter} = this.props;
    const {useExperiments} = this.props.globals;
    const id = useExperiments.enabled(SPECS.StickyAddToCartMobile)
      ? this.props.id
      : `${VIEW_MORE_OPTIONS_URL_FRAGMENT}-${colorPickerCounter}`;

    const selectionIds = this.productOption.selections.map((selection) => selection.id).join('_');
    const titleId = `product-colors-title-${selectionIds}`;

    return (
      <div
        id={id}
        data-hook={ProductColorsDataHook.Container}
        aria-labelledby={titleId}
        className={s.uiTpaColoPicker}
        ref={this.colorPickerContainerRef}>
        <div className={s.actionable}>{this.renderColorPicker()}</div>
      </div>
    );
  }
}

export const ProductColors = withGlobalProps(
  withTranslations('globals.texts')(withRenderContext(ProductColorsComponent))
);
